// W03/W04/W05 outlined fields (legacy BaseInput / MoneyInput / TaxInput over q-input outlined): 56 px (dense 40),
// radius 8, 1 px border that turns primary on hover and 2 px primary on focus, a label that floats to the top at
// 0.85 scale and weight 500 once the field has a value or focus, 10 px messages below.
import { forwardRef, useId, useLayoutEffect, useRef, useState, type InputHTMLAttributes, type MouseEvent, type ReactElement, type ReactNode, type RefObject } from 'react';
import { CircleX } from 'lucide-react';
import { settleFocusAfterClear } from './platform.ts';

interface FieldFrameProps {
  readonly id: string;
  readonly label: string;
  readonly floated: boolean;
  /**
   * A native date or time input's label (B650): Quasar lifts it with its own `.q-field__label:has(+ .q-field__native[type=date])`
   * rule, translateY(-40%) scale(.75) (dense -30%), not `.q-field--float`: grey at weight 400 while empty, --q-text-primary at 500
   * once filled or focused, still .75 because that rule outranks `.q-field--float`.
   */
  readonly nativeLabel?: 'rest' | 'lit' | undefined;
  readonly focused: boolean;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly dense?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  /** How a disabled box looks: a q-field's (content at .6) or legacy TimeInput's (the whole box at .6 as well; B689). */
  readonly disabledLook?: 'field' | 'time-input' | undefined;
  readonly dashed?: boolean | undefined;
  /** Grows with its content past the 40/56 px height (a q-select `use-chips` whose chips wrap; Quasar's auto-height field). */
  readonly autoHeight?: boolean | undefined;
  readonly start?: ReactNode;
  readonly end?: ReactNode;
  readonly className?: string | undefined;
  /**
   * Wraps the bordered box alone, not its hint or error line: a menu anchored to the field hangs from the box's bottom
   * edge, as a q-select's does (lane V r22: Payroll's employee menu hung 31 px low from the whole field).
   */
  readonly wrapBox?: ((box: ReactElement) => ReactNode) | undefined;
  /**
   * Legacy's plain label over the box (a `<label>` above a q-input or q-select that has none of its own, as API Keys and
   * Voice Ordering draw them, B533): medium weight, 4 px above the box, or 8 px with 'loose' (a q-gutter-sm column);
   * 'plain' is a plain div's label at normal weight, 4 px above (the QR template panel's selects, B1386).
   * Nothing floats inside the box, and the label stays the field's accessible name.
   */
  readonly labelAbove?: boolean | 'loose' | 'plain' | undefined;
  /** The label for assistive tech only, nothing drawn in or over the box (legacy's BaseInput with only a placeholder, B1173). */
  readonly hideLabel?: boolean | undefined;
  readonly children: ReactNode;
}

/**
 * Quasar's error icon (B1918). use-field.js pushes it as an inner append whenever the field has an error and isn't
 * `no-error-icon`. Legacy sets that on TableInput2 (which draws its own `error` icon in its append slot) and on
 * MenuBoardPlaylists' duration, transition and select fields, none of which has a Next field yet: a Next port of either
 * must not draw this one. It is Material's `error` at the marginal 24 px
 * in --q-negative, after the value and its suffix and before any append. Its `.q-field__append` pads 12 px (dense 6 px) in
 * place of the frame's 8 px gap, and an append after it pads 2 px (FIELD_APPEND_AFTER_ERROR).
 *
 * Walked on legacy Edit User's emptied Name at 1600: the icon sits 12 px in from the box's right edge, and the value's box
 * is 36 px narrower.
 */
export function FieldErrorIcon({ dense, top }: { readonly dense: boolean | undefined; readonly top: boolean }) {
  // A growing field keeps it in the 56 px (dense 40) marginal at the box's top, as its other marginals.
  const place = top ? `self-start ${dense === true ? 'h-[38px]' : 'h-[54px]'}` : '';
  return (
    <span data-field-error-icon="" className={`-ml-2 flex shrink-0 items-center text-negative ${dense === true ? 'pl-[6px]' : 'pl-3'} ${place}`}>
      <svg aria-hidden width={24} height={24} viewBox="0 0 24 24" data-testid="field-error-icon">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor" />
      </svg>
    </span>
  );
}

/** Quasar's `.q-field__append + .q-field__append { padding-left: 2px }`: an append that follows the error icon pads 2 px, not 12 (B1918). */
export const FIELD_APPEND_AFTER_ERROR = '[[data-field-error-icon]+&]:pl-[2px] [[data-field-error-icon]+*>&]:pl-[2px]';

/** The label `labelAbove` draws over a field's box. */
function LabelAbove({ id, label, kind }: { readonly id: string; readonly label: string; readonly kind: true | 'loose' | 'plain' }) {
  return <label htmlFor={id} className={`block text-[14px] ${kind === 'plain' ? 'font-normal' : 'font-medium'} text-text-2 ${kind === 'loose' ? 'pb-2' : 'pb-1'}`}>{label}</label>;
}

/**
 * The outlined frame every field shares: border, floating label, adornments and the message line. Its 11 px side padding
 * plus the 1 px border puts the text 12 px in from the outer edge, as legacy's q-field (its outline is drawn outside the
 * box, lane D5 r34: Next's text sat 1 px right of legacy's on every field).
 */
/**
 * Legacy q-field__bottom: a 10 px line (Quasar's line-height 1), so a hint that wraps puts its lines 10 px apart, in a
 * line at least 20 px tall whose first line sits where a one-line hint always has (B550). Legacy's app.scss pads it
 * 3 px a side (`.q-field__bottom { padding-left: 3px; padding-right: 3px }` over Quasar's 12), so a hint or error starts
 * 3 px in from the box's edge, as TextAreaField's already does (B806: Next's sat 9 px right of legacy's). Its padding-top is
 * Quasar's 8 px, as TextAreaField's (B1211: at 7 every hint and error sat 1 px high, and a two-line hint's row was 27 px
 * against legacy's 28; measured on Order Lookup's Order ID and Business Profile's Location Name at 390, 1600 and 1920).
 */
const FIELD_BOTTOM = 'min-h-5 px-[3px] pt-2 text-[10px] leading-[10px]';

/**
 * A disabled field's box, as Quasar's (B689): `.q-field--disabled .q-field__control > div { opacity: .6 }` fades only what is
 * inside the box (label, value, adornments), the outline and the fill stay at full strength, the box takes no pointer
 * (`.q-field__control { pointer-events: none }`, so no hover outline) and `.q-field__inner { cursor: not-allowed }`.
 */
export const FIELD_DISABLED_BOX = 'cursor-not-allowed [&>*]:pointer-events-none [&>*]:opacity-60';

/**
 * A field's marginal controls (the drop arrow, the clear button, an append's icon) in Quasar's `.q-field__marginal` colour,
 * rgba(0,0,0,.54) (dark rgba(255,255,255,.7)). The primary while their field is focused is not Quasar's: it is legacy's own
 * unscoped rule in MoneyInput.vue:125 (`.q-field--focused .q-field__append i`, and _dark.scss:159), which reaches every
 * q-field's append icon once MoneyInput has loaded: open, or holding focus (after a pick or Escape; a click elsewhere blurs it). The field marks itself `group/field`, and `data-focused` while open (walked on legacy Item Sales'
 * Category and Columns selects, lane D5 r35).
 */
export const FIELD_MARGINAL = 'text-black/[0.54] dark:text-white/70 group-data-[focused]/field:text-primary! group-has-[:focus]/field:text-primary! group-focus/field:text-primary!';

/**
 * Legacy's TimeInput (`components/form/TimeInput.vue`, a vue3-datepicker around a q-input) disables differently: the picker's
 * wrapper takes Quasar's global `.disabled { opacity: .6 }`, so the whole box fades, outline and fill included, and the
 * q-field inside fades its content by .6 again (B689; Special Hours' From and To, Business Hours' closed days).
 */
const TIME_INPUT_DISABLED_BOX = `opacity-60 ${FIELD_DISABLED_BOX}`;

export function FieldFrame({ id, label, floated, nativeLabel, focused, error, hint, dense, disabled, disabledLook = 'field', dashed, autoHeight = false, start, end, className = '', wrapBox, labelAbove, hideLabel = false, children }: FieldFrameProps) {
  const above = labelAbove !== undefined && labelAbove !== false;
  const invalid = error !== undefined && error !== '';
  // B1918: Quasar's `.q-field--highlighted .q-field__label { color: currentColor }` (the control's text-negative on an error)
  // loses to legacy app.scss's later `.q-field--float .q-field__label { color: var(--q-text-primary) }`, so an invalid label
  // is red only while it rests in the box, and keeps its ink once it floats (walked on legacy Edit User and Register Settings).
  const labelUp = nativeLabel === undefined ? floated : nativeLabel === 'lit';
  const border = invalid
    ? 'border-negative ring-1 ring-negative ring-inset'
    : focused
      ? 'border-primary ring-1 ring-primary ring-inset'
      : `border-line ${disabled ? '' : 'hover:border-primary has-[:focus]:border-primary has-[:focus]:ring-1 has-[:focus]:ring-primary has-[:focus]:ring-inset'}`;
  const box = (
    <div
      data-field-box=""
      data-focused={focused === true && !invalid ? '' : undefined}
      data-invalid={invalid ? '' : undefined}
      className={`group/field relative flex items-center gap-2 rounded-q border bg-field px-[11px] transition-colors ${dashed ? 'border-dashed' : ''} ${autoHeight ? (dense ? 'min-h-10' : 'min-h-14') : dense ? 'h-10' : 'h-14'} ${border} ${disabled ? (disabledLook === 'time-input' ? TIME_INPUT_DISABLED_BOX : FIELD_DISABLED_BOX) : ''}`}
    >
      {start}
      <div className="relative flex h-full min-w-0 flex-1 items-center">
        {above ? null : hideLabel ? <label htmlFor={id} className="sr-only">{label}</label> : <label
          htmlFor={id}
          className={`pointer-events-none absolute left-0 origin-top-left truncate transition-all duration-150 ${
            // Dense: Quasar's .q-field--dense label, top 10 px, translateY(-30%) scale(.75) once floated (lane V r24: ours sat
            // 8 px high, touching the border). Standard: Quasar's label top 18 px from the box's outer edge with a 17.5 px line,
            // so translateY(-40%) lifts it 7 px to 11 px in (lane D4 r26, measured on the timecard editor: ours sat 8.4 px high).
            nativeLabel !== undefined
              ? `${dense ? 'top-[9px]' : 'top-[17px]'} text-[14px] leading-[17.5px] ${nativeLabel === 'lit' ? 'rb-field-label-native-lit' : 'rb-field-label-native'}${dense ? ' rb-field-label-native-dense' : ''}`
              : floated ? (dense ? 'top-[9px] text-[14px] leading-[17.5px] rb-field-label-floating-dense' : 'top-[17px] text-[14px] leading-[17.5px] rb-field-label-floating')
                // At rest Quasar's label keeps the same top and 17.5 px line (B1127): centring a 21 px line put it 1.25 px low.
                // An invalid one is negative in place of the grey (B1918, #2933 review): both are colour utilities, and the
                // compiled sheet puts text-text-2 after text-negative, so carrying both drew the label grey.
                : `${dense ? 'top-[9px]' : 'top-[17px]'} text-[14px] leading-[17.5px] ${invalid ? 'text-negative' : FIELD_LABEL_INK}`
          } ${
            // Quasar's .q-field__label (B1105): max-width 100% at rest and 133% once the field is --float (the box's width at
            // the dense .75 scale; 113% of it at legacy's standard .85, app.scss), and letter-spacing 0.00937em where the body's
            // is 0.25px. A label longer than its box ends in an ellipsis inside it rather than running past the border. An
            // empty, unfocused date or time field isn't --float (Quasar's :has rule only moves the label), so it caps at 100%.
            labelUp ? 'max-w-[133%]' : 'max-w-full'
            // A resting native date or time label's grey is a components-layer class, which the utility outranks.
          } tracking-[0.00937em] ${invalid && nativeLabel === 'rest' ? 'text-negative' : ''}`}
        >
          {label}
        </label>}
        {children}
      </div>
      {invalid ? <FieldErrorIcon dense={dense} top={autoHeight} /> : null}
      {/* Quasar's .q-field__marginal is 56 px (dense 40) from the box's top, so a growing field keeps its arrow there. */}
      {end === undefined || !autoHeight ? end : <div className={`flex items-center self-start ${dense ? 'h-[38px]' : 'h-[54px]'}`} data-testid="field-end">{end}</div>}
    </div>
  );
  return (
    <div className={className}>
      {above ? <LabelAbove id={id} label={label} kind={labelAbove} /> : null}
      {wrapBox === undefined ? box : wrapBox(box)}
      <FieldMessage id={id} error={error} hint={hint} disabled={disabled} />
    </div>
  );
}

/** Quasar's q-field__bottom: the error (negative, announced) in place of the hint, `${id}-message` for aria-describedby. */
export function FieldMessage({ id, error, hint, disabled }: { readonly id: string; readonly error?: string | undefined; readonly hint?: string | undefined; readonly disabled?: boolean | undefined }) {
  if (error !== undefined && error !== '') return <p id={`${id}-message`} role="alert" className={`${FIELD_BOTTOM} text-negative ${disabled === true ? 'cursor-not-allowed' : ''}`}>{error}</p>;
  // Quasar's dark .q-field__bottom is 70 % white, the secondary ink (B1225); light keeps the AA tertiary.
  if (hint !== undefined) return <p id={`${id}-message`} className={`${FIELD_BOTTOM} text-text-3 dark:text-text-2 ${disabled === true ? 'cursor-not-allowed' : ''}`}>{hint}</p>;
  return null;
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'prefix'> {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly dense?: boolean;
  readonly prefix?: string;
  /**
   * Quasar's `suffix` (q-field__suffix): text on the value's own line, 4 px after the value, in the value's ink, and
   * transparent until the label floats, as legacy's `%`, `Minutes`, `min` and `.parsepronto.com` (B1100: Next centred it
   * in the box's end slot, 7 px above legacy's). An icon or button goes in `append`.
   */
  readonly suffix?: string;
  /** Quasar's append slot: an icon or button at the box's end, centred in its height (a clear, a copy, a picker). */
  readonly append?: ReactNode;
  readonly start?: ReactNode;
  readonly frameClassName?: string;
  /** Legacy's read-only outline (a dashed border). */
  readonly dashed?: boolean;
  /**
   * Legacy's plain label over the box (a `<label>` above a q-input or q-select that has none of its own, as API Keys and
   * Voice Ordering draw them, B533): medium weight, 4 px above the box, or 8 px with 'loose' (a q-gutter-sm column);
   * 'plain' is a plain div's label at normal weight, 4 px above (the QR template panel's selects, B1386).
   * Nothing floats inside the box, and the label stays the field's accessible name.
   */
  readonly labelAbove?: boolean | 'loose' | 'plain' | undefined;
  /** The prefix and value in legacy's tertiary ink (MoneyInput's `ui-money-input-empty` at 0, B807), its placeholder at full strength (B1452). */
  readonly muted?: boolean;
  /**
   * Quasar's `stack-label`: the label stays floated, and the prefix, suffix and placeholder show, even while the field is
   * empty and unfocused. ScaledField sets it: legacy's MoneyInput fills its mask with zeros, so its field always holds a
   * value ("0.00") and is always --float (B1137).
   */
  readonly stackLabel?: boolean;
  /**
   * The label for assistive tech only (B1173): legacy's BaseInput with a placeholder and no label, as Pricing's Unit Name
   * and ID Verification's name. A q-field without a label isn't --labeled, so its placeholder, prefix and suffix always
   * show, and the value sits centred in the box with no room kept for a label.
   */
  readonly hideLabel?: boolean;
}

/**
 * Quasar's placeholder (`.q-field__native::placeholder { color: inherit; opacity: .7 }`): the value's own ink at 70 %, in
 * either theme, where Tailwind's preflight leaves the input's colour at 50 % (B1181: about #8f8f8f on white, under AA).
 */
export const PLACEHOLDER_INK = 'placeholder:text-inherit placeholder:opacity-70';

/**
 * Legacy's Quasar field inks (B2056): its resting label is rgba(0,0,0,.6) and its typed text (`.q-field__native`, a
 * q-select's chosen value too) rgba(0,0,0,.87) in light, where Next's opaque text-2 (#646866) and text-1 (#1f1f1f) drew
 * them a shade off; in dark they are white .7 and .87, which are text-2 and text-1.
 */
const FIELD_LABEL_INK = 'text-black/60 dark:text-text-2';
export const FIELD_TEXT_INK = 'text-black/[0.87] dark:text-text-1';

/** Input types whose browser editor draws its own hint ("mm/dd/yyyy", "--:-- --") in an empty field. */
const NATIVE_HINT_TYPES: ReadonlySet<string> = new Set(['date', 'time', 'datetime-local', 'month', 'week']);

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, value, onValueChange, error, hint, dense, prefix, suffix, append, start, frameClassName, dashed, labelAbove, muted = false, stackLabel = false, hideLabel = false, id: givenId, onFocus, onBlur, disabled, ...rest },
  ref,
) {
  const generated = useId();
  const id = givenId ?? generated;
  const [focused, setFocused] = useState(false);
  // Quasar's .q-field--float is focus or a value (or stack-label), never a prefix or a placeholder (B1137): at rest a labelled
  // field keeps its label in the box and hides its prefix, suffix and placeholder (`.q-field--labeled:not(.q-field--float)`:
  // prefix and suffix at opacity 0, the placeholder transparent), so a placeholder never overlaps the label (V 2026-10-12).
  // B650: a native date or time input's own hint ("mm/dd/yyyy", "--:-- --"), which the browser draws even while the field is
  // empty, floats it: legacy's BaseInput (a q-input of that type, walked on Time Off › Add Time Off) shows the label small at
  // the top with the hint under it. A half-typed date stays in view the same way.
  const nativeHint = NATIVE_HINT_TYPES.has(rest.type ?? '');
  const floated = focused || value !== '' || nativeHint || stackLabel;
  const described = (error !== undefined && error !== '') || hint !== undefined ? `${id}-message` : undefined;
  const above = labelAbove !== undefined && labelAbove !== false;
  // Quasar counts neither a label above the box nor no label as --labeled: nothing fades at rest and no label room is kept.
  const unlabeled = above || hideLabel;
  return (
    <FieldFrame
      hideLabel={hideLabel} id={id} label={label} floated={floated} nativeLabel={nativeHint ? (focused || value !== '' ? 'lit' : 'rest') : undefined} focused={focused} error={error} hint={hint} dense={dense} disabled={disabled} dashed={dashed} labelAbove={labelAbove}
      // Quasar's .q-field__append (B1134): 12 px after the value (6 px dense) in place of the frame's 8 px gap, 2 px between
      // two, a 24 px marginal font and its rgba(0,0,0,.54) ink, primary while the field holds focus. A caller's own ink wins.
      start={start} end={append === undefined ? undefined : <span className={`-ml-2 flex shrink-0 items-center gap-[2px] text-[24px] ${dense === true ? 'pl-[6px]' : 'pl-3'} ${FIELD_APPEND_AFTER_ERROR} ${FIELD_MARGINAL}`} data-testid="field-append">{append}</span>} className={frameClassName}
    >
      {/* Quasar's .q-field__native, __prefix and __suffix set letter-spacing 0.00937em over the body's 0.25 px (B1110). */}
      <div className={`flex w-full items-center gap-1 ${unlabeled ? '' : dense ? 'pt-3' : 'pt-4'}`}>
        {/* Quasar's prefix keeps its width at rest and fades in once the label floats, as the suffix does (B1137). Both take
            the value's ink, rgba(0,0,0,.87) in light (Quasar's .q-field__prefix / __suffix; B2056). */}
        {prefix === undefined ? null : (
          <span data-testid="field-prefix" className={`shrink-0 whitespace-nowrap text-[14px] tracking-[0.00937em] ${muted ? 'text-text-3' : FIELD_TEXT_INK} transition-opacity duration-[360ms] ${floated || unlabeled ? '' : 'opacity-0'}`}>{prefix}</span>
        )}
        <input
          ref={ref}
          id={id}
          value={value}
          disabled={disabled}
          aria-invalid={error !== undefined && error !== '' ? true : undefined}
          aria-describedby={described}
          onChange={(e) => onValueChange(e.target.value)}
          onFocus={(e) => { setFocused(true); onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); onBlur?.(e); }}
          className={`w-full min-w-0 bg-transparent text-[14px] tracking-[0.00937em] ${muted ? 'text-text-3' : FIELD_TEXT_INK} outline-none focus-visible:outline-none ${floated || unlabeled ? (muted ? 'placeholder:text-inherit' : PLACEHOLDER_INK) : 'placeholder:text-transparent'}`}
          {...rest}
        />
        {/* Quasar's .q-field--labeled:not(.q-field--float) .q-field__suffix { opacity: 0 }: hidden, keeping its width, until
            the label floats; a field labelled above its box isn't --labeled, so its suffix always shows. */}
        {suffix === undefined ? null : (
          <span data-testid="field-suffix" className={`shrink-0 whitespace-nowrap text-[14px] tracking-[0.00937em] ${FIELD_TEXT_INK} transition-opacity duration-[360ms] ${floated || unlabeled ? '' : 'opacity-0'}`}>{suffix}</span>
        )}
      </div>
    </FieldFrame>
  );
});

interface TextAreaFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly hint?: string;
  readonly maxLength?: number;
  readonly disabled?: boolean;
  readonly rows?: number;
  /** The label for assistive tech only, no floating label (legacy textareas without one). */
  readonly hideLabel?: boolean;
  /** Show "{n} / {max}" under the box (legacy BaseInput `counter`); off unless asked, as legacy (B560). */
  readonly counter?: boolean;
  /** Legacy `autogrow`: one row to start, as tall as what is typed, no resize grip. */
  readonly autogrow?: boolean;
  /**
   * Legacy `dense` on a labelled textarea (Quasar's .q-field--dense .q-textarea, B2027): the text 17 px below the box's top
   * edge and 4 px above its bottom (measured on the snapshot's Store Info), so two rows make a 57 px box; the label sits at
   * 9 px and floats at the dense .75 scale. A box without a label keeps the standard metrics.
   */
  readonly dense?: boolean;
  readonly placeholder?: string;
  /**
   * Legacy's plain label over the box (a `<label>` above a q-input or q-select that has none of its own, as API Keys and
   * Voice Ordering draw them, B533): medium weight, 4 px above the box, or 8 px with 'loose' (a q-gutter-sm column);
   * 'plain' is a plain div's label at normal weight, 4 px above (the QR template panel's selects, B1386).
   * Nothing floats inside the box, and the label stays the field's accessible name.
   */
  readonly labelAbove?: boolean | 'loose' | 'plain' | undefined;
}

/**
 * BaseInput type="textarea" with `counter`: an outlined box, the hint left and "{n} / {max}" right below it. Its
 * metrics are Quasar 2's .q-textarea (B531): an 18 px line, the text 27 px below a labelled box's top edge (19 px
 * without a label) and 10 px (8 px) above its bottom, two rows unless told otherwise, and 56 px at the least.
 */
export function TextAreaField({ label, value, onValueChange, hint, maxLength, disabled, rows = 2, hideLabel: hidden = false, counter = false, autogrow = false, dense = false, placeholder, labelAbove }: TextAreaFieldProps) {
  const above = labelAbove !== undefined && labelAbove !== false;
  // A label above the box leaves the box as an unlabelled one inside.
  const hideLabel = hidden || above;
  const denseLabelled = dense && !hideLabel;
  const id = useId();
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  // Legacy autogrow: the box follows what is typed (the height is reset first so it can shrink too).
  useLayoutEffect(() => {
    const area = ref.current;
    if (!autogrow || area === null) return;
    area.style.height = 'auto';
    area.style.height = `${String(area.scrollHeight)}px`;
  }, [autogrow, value]);
  const counted = maxLength !== undefined && counter;
  const floated = focused || value !== '';
  const border = focused ? 'border-primary ring-1 ring-primary ring-inset' : `border-line ${disabled === true ? '' : 'hover:border-primary'}`;
  return (
    <div>
      {above ? <LabelAbove id={id} label={label} kind={labelAbove} /> : null}
      <div data-field-box="" className={`relative ${denseLabelled ? 'min-h-10' : 'min-h-[56px]'} rounded-q border bg-field px-[11px] ${denseLabelled ? 'pt-4 pb-[3px]' : hideLabel ? 'pt-[18px] pb-[7px]' : 'pt-[26px] pb-[9px]'} transition-colors ${border} ${disabled === true ? FIELD_DISABLED_BOX : ''}`}>
        {above ? null : <label
          htmlFor={id}
          className={hideLabel ? 'sr-only' : `pointer-events-none absolute left-[11px] ${denseLabelled ? 'top-[9px]' : 'top-[17px]'} origin-top-left text-[14px] leading-[17.5px] transition-all duration-150 ${floated ? (denseLabelled ? 'rb-field-label-floating-dense' : 'rb-field-label-floating') : FIELD_LABEL_INK} tracking-[0.00937em]`}
        >
          {label}
        </label>}
        <textarea
          ref={ref}
          id={id}
          rows={autogrow ? 1 : rows}
          placeholder={placeholder}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          aria-describedby={hint === undefined ? undefined : `${id}-message`}
          onChange={(e) => onValueChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`block w-full ${autogrow ? 'resize-none overflow-hidden' : 'resize-y'} bg-transparent text-[14px] leading-[18px] tracking-[0.00937em] ${FIELD_TEXT_INK} outline-none ${PLACEHOLDER_INK}`}
        />
      </div>
      {/* Legacy q-field__bottom: 8 px under the box, line-height 1, so the hint runs on 10 px lines and the counter (8 px
          from it) on a 12 px one, its ink 10 px under the box's edge as in legacy's Create Customer Notes. Quasar leaves
          the row out when there is neither a hint nor a counter, so nothing sits under the box then. */}
      {hint === undefined && !counted ? null : (
        <div data-testid="field-bottom" className={`flex min-h-5 items-start justify-between px-[3px] pt-2 text-[10px] leading-[10px] text-text-3 dark:text-text-2 ${disabled === true ? 'cursor-not-allowed' : ''}`}>
          <p id={`${id}-message`} className="min-w-0 flex-1">{hint}</p>
          {counted ? <span className="shrink-0 pl-2 text-[12px] leading-3">{`${String(value.length)} / ${String(maxLength)}`}</span> : null}
        </div>
      )}
    </div>
  );
}

/** `104n` at 2 decimals → "1.04"; reverse-filled like the legacy q-input mask `#.##` with fill `0`. */
export function formatScaled(value: bigint, decimals: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(decimals + 1, '0');
  const text = decimals === 0 ? digits : `${digits.slice(0, digits.length - decimals)}.${digits.slice(digits.length - decimals)}`;
  return negative ? `-${text}` : text;
}

/**
 * The digits a person typed, read as a scaled integer (cash-register entry: 1, 2, 3, 4 → 12.34). No significant digit
 * (blank, or backspaced down to zeros) is null: the caller decides whether that is blank or 0.
 */
export function parseScaled(text: string, maxDigits: number): bigint | null {
  const digits = text.replace(/\D/g, '').replace(/^0+/, '').slice(-maxDigits);
  return digits === '' ? null : BigInt(digits);
}

interface ScaledFieldProps extends Omit<TextFieldProps, 'value' | 'onValueChange' | 'inputMode'> {
  /** The amount in its smallest unit (cents, basis points); null is blank. */
  readonly value: bigint | null;
  readonly onValueChange: (value: bigint | null) => void;
  readonly decimals?: number;
  /** Blank stays blank (an open amount) instead of becoming 0. */
  readonly nullable?: boolean;
  readonly maxDigits?: number;
  /**
   * Grey the prefix and the amount while it is 0, as legacy MoneyInput (`ui-money-input-empty`, --q-text-tertiary; B807).
   * The rule is "legacy draws a MoneyInput here": on by default for a "$" field (every one is); a "%" field that legacy
   * draws with MoneyInput (the cash discount rate, the gratuity rate) passes it, while TaxInput and PercentInput ones stay
   * in body ink (PercentInput names an undefined colour).
   */
  readonly muteZero?: boolean;
  /**
   * Grey an empty field too. Only for a field legacy stores as 0 when blank (the discount amounts: legacy MoneyInput greys
   * its "$ 0.00" mask at 0). Legacy greys by value 0, not by emptiness: a null legacy filter (Order Lookup's Min / Max
   * Amount) keeps body ink, so this is opt-in (B1452).
   */
  readonly muteEmpty?: boolean;
}

/** W04 MoneyInput / W05 PercentInput: integer smallest units in, integer smallest units out, never a float. */
export const ScaledField = forwardRef<HTMLInputElement, ScaledFieldProps>(function ScaledField(
  { value, onValueChange, decimals = 2, nullable = false, maxDigits = 9, placeholder, muteZero, muteEmpty = false, ...rest },
  ref,
) {
  const text = value === null ? '' : formatScaled(value, decimals);
  return (
    <TextField
      ref={ref}
      {...rest}
      inputMode="numeric"
      stackLabel
      // muteEmpty: an empty field draws legacy MoneyInput's zero mask ("$ 0.00") in the same tertiary ink as a 0 amount (B1452).
      muted={(muteZero ?? rest.prefix === '$') && (value === 0n || (muteEmpty && value === null))}
      placeholder={placeholder ?? formatScaled(0n, decimals)}
      value={text}
      onValueChange={(next) => {
        const parsed = parseScaled(next, maxDigits);
        onValueChange(parsed === null ? (nullable ? null : 0n) : parsed);
      }}
      onFocus={(e) => {
        const input = e.currentTarget;
        requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
      }}
    />
  );
});

/**
 * Quasar's clear icon (`cancel`, a filled circle with a cut-out cross) on a `clearable` q-select: 24 px, the field's
 * marginal colour (rgba(0,0,0,.54), dark rgba(255,255,255,.7)) at 0.6 opacity, full on hover or focus, as
 * `.q-field__focusable-action` (walked on legacy Menu › Items' Category filter, lane D5 r34). Also a `clearable` q-input's
 * (a customer's Orders filters, B1007). After `onClear`, focus settles as Quasar's clearValue (use-field.js 325-335): on a
 * desktop browser it goes back to `refocus`, the field, rather than dropping to the page with the unmounted button (B1024);
 * on a mobile browser (Quasar's user-agent test, `isMobileBrowser`) a focus left in the field is blurred, so the
 * on-screen keyboard closes rather than opening (B1028). `ink` replaces the marginal colour where a legacy page inks the
 * field's icons itself (Order Lookup's `.order-number-input .q-icon`, B2045).
 */
export function ClearButton({ label, onClear, refocus, ink = FIELD_MARGINAL }: {
  readonly label: string; readonly onClear: () => void; readonly refocus: RefObject<HTMLElement | null>; readonly ink?: string;
}) {
  const clear = (event: MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    onClear();
    settleFocusAfterClear(refocus.current, button);
  };
  return (
    <button
      type="button" aria-label={`Clear ${label}`} onClick={clear}
      className={`inline-flex rounded-full opacity-60 hover:opacity-100 focus-visible:opacity-100 ${ink}`}
    >
      <CircleX size={24} aria-hidden fill="currentColor" className="[&>path]:stroke-surface [&>path]:stroke-[2.5]" />
    </button>
  );
}

/**
 * The list pages' "Search..." box (PageHeaderAlternate): dense, outlined, the placeholder doubles as its visible cue. Its
 * text starts 12 px in, as Quasar's .q-field__control padding with the outline drawn inside it: 11 px past Next's 1 px
 * border, as TextField's (B1493; px-3 put it 13 px in).
 */
export function SearchField({ value, onValueChange, label = 'Search', className = '', placeholder = 'Search...', wide = false, clearable = false }: {
  readonly value: string; readonly onValueChange: (value: string) => void; readonly label?: string; readonly className?: string;
  /** Legacy pages whose search box shows a magnifier instead pass ''. */
  readonly placeholder?: string;
  /**
   * From 600 px (Quasar's sm, B1216) a search box is 177 px wide, a q-input with no width of its own. `wide` makes it 180 from 600 (Quasar's sm, B1121): legacy's list headers
   * (PageHeaderAlternate) give their search's `ui-header-input` class a 180 px minimum (B984). Legacy's menu lists, Preset
   * Discounts, Floor Plans and Admin › Users meant the same but pass the class through BaseInput's `classes` prop, which
   * BaseInput binds as `{ classes }` and so drops, leaving them at 177: a legacy bug that Next fixes (B1020), so they pass
   * `wide` too. Time Segments and the consolidated report never had the class and stay 177.
   */
  readonly wide?: boolean;
  /**
   * Legacy's BaseInput `clearable` (the employee list searches, B2037): once the box holds text, Quasar's `cancel` glyph
   * (ClearButton) 12 px from its right edge and 8 px down, the text stopping 6 px before it (dense `.q-field__append`).
   * Legacy's box grows from 180 to 207 px as the cross appears, shoving the filters beside it right; Next keeps the box's
   * width and narrows the text instead (legacy bug B2050, ledger L1141).
   */
  readonly clearable?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const width = wide ? 'min-[600px]:w-[180px]' : 'min-[600px]:w-[177px]';
  const filled = clearable && value !== '';
  // B2037: a `type="search"` box made Chrome draw its own blue cancel cross once text was typed, and Escape emptied it;
  // legacy's q-input is a text box that does neither. The role keeps it a search box for assistive technology.
  const box = (
    <input
      ref={input}
      type="text"
      role="searchbox"
      aria-label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      className={`h-10 w-full rounded-q border border-line bg-field ${filled ? 'pr-[42px] pl-[11px]' : 'px-[11px]'} text-[14px] tracking-[0.00937em] ${FIELD_TEXT_INK} outline-none ${PLACEHOLDER_INK} hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary focus:ring-inset ${clearable ? '' : `${width} ${className}`}`}
    />
  );
  if (!clearable) return box;
  return (
    <div className={`group/field relative w-full ${width} ${className}`}>
      {box}
      {filled ? <span className="absolute top-2 right-3 inline-flex"><ClearButton label={label} onClear={() => onValueChange('')} refocus={input} /></span> : null}
    </div>
  );
}
